import { useEffect, useState } from 'react';
import api from '../utils/api';

export interface SiteContentRow {
  id: string;
  key: string;
  label: string;
  value: string;
  isVisible: boolean;
  section: string;
  sortOrder: number;
}

let cache: Record<string, string> | null = null;
let inflight: Promise<Record<string, string>> | null = null;
const subscribers = new Set<(m: Record<string, string>) => void>();

async function fetchContent(): Promise<Record<string, string>> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await api.get('/site-content');
      const rows: SiteContentRow[] = Array.isArray(res.data) ? res.data : [];
      const map: Record<string, string> = {};
      for (const r of rows) {
        if (r.isVisible) map[r.key] = r.value;
      }
      cache = map;
      subscribers.forEach(fn => fn(map));
      return map;
    } catch {
      cache = {};
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Returns a `c(key, fallback)` function. On first call, kicks off a single
 * network request; subsequent pages share the same cache for one tab session.
 * Admin saves can call `invalidateSiteContent()` to force a refetch.
 *
 * Every marketing string on the public site should use this with a hardcoded
 * fallback that matches the seed value exactly — fresh installs with an empty
 * DB must still render a complete site.
 */
export function useSiteContent() {
  const [map, setMap] = useState<Record<string, string>>(cache ?? {});

  useEffect(() => {
    if (!cache) fetchContent().then(setMap);
    const sub = (m: Record<string, string>) => setMap(m);
    subscribers.add(sub);
    return () => { subscribers.delete(sub); };
  }, []);

  return function c(key: string, fallback: string = ''): string {
    const v = map[key];
    return v !== undefined && v !== '' ? v : fallback;
  };
}

export function invalidateSiteContent() {
  cache = null;
  fetchContent();
}
